export interface SamplePrevisitVisitNote {
  id: string;
  date: string;
  type: string;
  title: string;
  data: {
    subjective: {
      chiefComplaint: string;
      anamnesis: string;
    };
    objective: string;
    assessment: string;
    plan: string;
  };
}

/**
 * Sample SOAP-shaped visit notes used by both the static Custom Insights
 * preview (web-vite) and the on-demand preview workflow (api-nest / mastra).
 *
 * Shared so the demo experience stays in sync across the static rendered
 * spec and a real LLM-generated preview run against the same source data.
 */
export const SAMPLE_PREVISIT_VISIT_NOTES: SamplePrevisitVisitNote[] = [
  {
    id: 'visit-1',
    date: '20 Dec 2025',
    type: 'Visit Note',
    title: 'Edward Hyde Visit Note',
    data: {
      subjective: {
        chiefComplaint:
          'Chief Complaint: Follow-up for diabetes and blood pressure.',
        anamnesis:
          'Feels well overall. Denies chest pain, SOB, dizziness. Diet inconsistent, not exercising regularly. Taking medications most days.',
      },
      objective: 'BP 142/86\nWt 94 kg\nA1c 7.4% (prior 7.1)\nLDL 118\nCMP wnl',
      assessment:
        'T2DM — suboptimal control\nHTN — borderline\nHLD — above goal',
      plan: 'Continue metformin, lisinopril, atorvastatin. Reviewed diet/exercise. Repeat labs in 6 months.',
    },
  },
  {
    id: 'visit-2',
    date: '20 Mar 2026',
    type: 'Visit Note',
    title: 'Edward Hyde Visit Note',
    data: {
      subjective: {
        chiefComplaint:
          'Chief Complaint: Follow-up for diabetes, BP, cholesterol.',
        anamnesis:
          'Reports increased fatigue over past few months. Denies chest pain or SOB. Not exercising. Taking medications inconsistently.',
      },
      objective:
        'BP 150/90\nWt 97 kg\nA1c 8.1%\nLDL 130\nASCVD risk discussed.',
      assessment:
        'T2DM — poorly controlled\nHTN — uncontrolled\nHLD — worsening',
      plan: 'Recommended GLP-1; patient hesitant. Increased lisinopril to 20 mg daily. Encouraged lifestyle changes. Labs in 3 months.',
    },
  },
  {
    id: 'visit-3',
    date: '15 Jun 2026',
    type: 'Visit Note',
    title: 'Edward Hyde Visit Note',
    data: {
      subjective: {
        chiefComplaint: 'Chief Complaint: Chest tightness with exertion.',
        anamnesis:
          'Reports intermittent substernal tightness over past week, occurs with stairs or carrying groceries, improves with rest. No nausea or diaphoresis. First time reporting chest symptoms.',
      },
      objective: 'BP 152/94\nHR 88\nWt 99 kg\nNo acute distress.',
      assessment:
        'Exertional chest discomfort — concerning given risk factors\nT2DM, HTN, HLD — poorly controlled',
      plan: 'Reviewed concern for possible cardiac etiology. Advised ED evaluation if symptoms worsen or recur. Patient hesitant but agrees to monitor closely.',
    },
  },
  {
    id: 'visit-4',
    date: '22 Jun 2026',
    type: 'Visit Note',
    title: 'Edward Hyde Visit Note',
    data: {
      subjective: {
        chiefComplaint: 'Chief Complaint: Chronic disease follow-up.',
        anamnesis:
          'No acute complaints. Admits diet has been poor due to work stress. Not checking blood sugars at home. Taking meds "most of the time."',
      },
      objective: 'BP 146/88\nWt 96 kg\nA1c 7.8%\nLDL 122',
      assessment:
        'T2DM — worsening control\nHTN — uncontrolled\nHLD — above goal',
      plan: 'Discussed adding second diabetes agent; patient prefers lifestyle trial first. Reinforced BP control. Follow-up in 3–4 months.',
    },
  },
  {
    id: 'visit-5',
    date: '20 Sep 2026',
    type: 'Visit Note',
    title: 'Edward Hyde Visit Note',
    data: {
      subjective: {
        chiefComplaint:
          'Chief Complaint: Follow-up for diabetes and blood pressure.',
        anamnesis:
          'Feels well overall. Denies chest pain, SOB, dizziness. Diet inconsistent, not exercising regularly. Taking medications most days.',
      },
      objective: 'BP 142/86\nWt 94 kg\nA1c 7.4% (prior 7.1)\nLDL 118\nCMP wnl',
      assessment:
        'T2DM — suboptimal control\nHTN — borderline\nHLD — above goal',
      plan: 'Continue metformin, lisinopril, atorvastatin. Reviewed diet/exercise. Repeat labs in 6 months.',
    },
  },
];

export const SAMPLE_PREVISIT_PATIENT_NAME = 'Edward Hyde';
